/**
 * Timeline Configuration - Safe Rollout Toggles
 *
 * Controls the migration from legacy collections to timeline-only mode.
 * These toggles allow for gradual rollout and easy rollback if needed.
 */

/**
 * Timeline configuration flags
 *
 * IMPORTANT: These should be environment-configurable in production
 * For now, they're hardcoded for development/testing
 */
const TIMELINE_CONFIG = {
  // Phase 2: Dual-write mode - write to both timeline and legacy collections
  TIMELINE_DUAL_WRITE: false,

  // Phase 4: Timeline-only mode - write only to timeline, read only from timeline
  TIMELINE_ONLY: true,

  // Optional: Shadow read mode - read from both paths for comparison/validation
  TIMELINE_SHADOW_READ: false,

  // Debug logging for timeline operations
  TIMELINE_DEBUG: true,
};

/**
 * Get current timeline configuration
 * @returns {Object} - Configuration object
 */
export function getTimelineConfig() {
  return { ...TIMELINE_CONFIG };
}

/**
 * Check if dual-write mode is enabled
 * @returns {boolean}
 */
export function isDualWriteEnabled() {
  return TIMELINE_CONFIG.TIMELINE_DUAL_WRITE;
}

/**
 * Check if timeline-only mode is enabled
 * @returns {boolean}
 */
export function isTimelineOnlyEnabled() {
  return TIMELINE_CONFIG.TIMELINE_ONLY;
}

/**
 * Check if shadow read mode is enabled
 * @returns {boolean}
 */
export function isShadowReadEnabled() {
  return TIMELINE_CONFIG.TIMELINE_SHADOW_READ;
}

/**
 * Check if timeline debug logging is enabled
 * @returns {boolean}
 */
export function isTimelineDebugEnabled() {
  return TIMELINE_CONFIG.TIMELINE_DEBUG;
}

/**
 * Update configuration (for testing/development)
 * IMPORTANT: In production, these should come from environment variables
 * @param {Object} updates - Configuration updates
 */
export function updateTimelineConfig(updates) {
  Object.assign(TIMELINE_CONFIG, updates);
  console.log("[TimelineConfig] Updated configuration:", TIMELINE_CONFIG);
}

/**
 * Reset configuration to defaults
 */
export function resetTimelineConfig() {
  TIMELINE_CONFIG.TIMELINE_DUAL_WRITE = false;
  TIMELINE_CONFIG.TIMELINE_ONLY = false;
  TIMELINE_CONFIG.TIMELINE_SHADOW_READ = false;
  TIMELINE_CONFIG.TIMELINE_DEBUG = true;
  console.log("[TimelineConfig] Reset to defaults");
}

/**
 * Get configuration for specific phase
 * @param {string} phase - Phase name ('dual-write', 'timeline-only', 'shadow-read')
 * @returns {Object} - Phase-specific configuration
 */
export function getPhaseConfig(phase) {
  switch (phase) {
    case "dual-write":
      return {
        TIMELINE_DUAL_WRITE: true,
        TIMELINE_ONLY: false,
        TIMELINE_SHADOW_READ: false,
      };
    case "timeline-only":
      return {
        TIMELINE_DUAL_WRITE: false,
        TIMELINE_ONLY: true,
        TIMELINE_SHADOW_READ: false,
      };
    case "shadow-read":
      return {
        TIMELINE_DUAL_WRITE: false,
        TIMELINE_ONLY: false,
        TIMELINE_SHADOW_READ: true,
      };
    default:
      return getTimelineConfig();
  }
}

/**
 * Apply phase configuration
 * @param {string} phase - Phase to activate
 */
export function activatePhase(phase) {
  const phaseConfig = getPhaseConfig(phase);
  updateTimelineConfig(phaseConfig);
  console.log(`[TimelineConfig] Activated phase: ${phase}`);
}
